import { AlertManager, useAlerts } from "client/Hooks";
import { ensureNotNil } from "client/Misc/utils";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { Action } from "client_legacy/ndaw/actions";
import { ImageFile, ImagePreviewDialog } from "../Dialogs/ImagePreview/ImagePreviewDialog";
import {
	getImagePreviewActions,
	getFileDownloadUrl,
	WebDavDocumentActionContext
} from "./actions";
import { useAddBookmarkDialog } from "../Bookmarks/AddBookmarkDialog";
import { DocumentStoreType } from "client/Api/Documents/common";
import { FileSystemItemModel, WebDavServerModel } from "client/Api/Documents/WebDav/webDav";

export type WebDavImagePreviewDialogProps = {
	serverId: number;
	initialImage: FileSystemItemModel;
	allImageItems: readonly FileSystemItemModel[];
	onClose: () => void;
	currentImageChangedCallback?: (file: FileSystemItemModel) => void;
	initialContext?: WebDavDocumentActionContext;
};

export const WebDavImagePreviewDialog = (props: WebDavImagePreviewDialogProps): ReactElement => {
	const {
		serverId,
		initialImage,
		allImageItems,
		onClose,
		currentImageChangedCallback,
		initialContext
	} = props;

	const [currentFileId, setCurrentFileId] = useState<string>(initialImage.id);

	const [addBookmarkDialog, openAddBookmarkDialog] = useAddBookmarkDialog();
	const alertManager: AlertManager = useAlerts();

	const currentFile: FileSystemItemModel = useMemo(() => {
			const file: FileSystemItemModel | undefined = allImageItems.find((item) => item.id === currentFileId);

			return ensureNotNil(file);
		},
		[allImageItems, currentFileId]
	);

	const currentImage = useMemo(
		(): ImageFile => convertFileToImage(currentFile, serverId),
		[currentFile, serverId]
	);

	useEffect(
		(): void => {
			currentImageChangedCallback?.(currentFile);
		},
		[currentFile, currentImageChangedCallback]
	);

	const allImages = useMemo(
		(): readonly ImageFile[] => allImageItems.map((x) => convertFileToImage(x, serverId)),
		[allImageItems, serverId]
	);

	const context = useMemo(
		(): WebDavDocumentActionContext | undefined => {
			if (!initialContext) {
				return undefined;
			}

			return {
				...initialContext,
				selectedDocuments: [currentFile]
			};
		},
		[currentFile, initialContext]
	);

	const addBookmarkHandler = useCallback(
		(server: WebDavServerModel, item: FileSystemItemModel): void => {
			openAddBookmarkDialog({
				store: DocumentStoreType.WebDav,
				serverId: server.id,
				item
			});
		},
		[openAddBookmarkDialog]
	);

	const actions = useMemo(
		(): readonly Action<WebDavDocumentActionContext>[] => {
			return getImagePreviewActions(
				alertManager,
				addBookmarkHandler
			);
		},
		[alertManager, addBookmarkHandler]
	);

	const changeImageHandler = (image: ImageFile): void => {
		const item: FileSystemItemModel | undefined = allImageItems.find(x => x.id === image.id);
		setCurrentFileId(
			ensureNotNil(item?.id)
		);
	};

	return (
		<>
			<ImagePreviewDialog
				onClose={onClose}
				currentImage={currentImage}
				allImages={allImages}
				onActiveImageChanged={changeImageHandler}
				context={context}
				actions={actions}
			/>

			{addBookmarkDialog}
		</>
	);
};
WebDavImagePreviewDialog.displayName = "WebDavImagePreviewDialog";

function convertFileToImage(file: FileSystemItemModel, serverId: number): ImageFile {
	return {
		id: file.id,
		name: file.name,
		url: getFileDownloadUrl(file.id, serverId)
	};
}
